import React, { useState } from 'react';

const AGENT_URL = 'https://essaness-search-agent-273538020820.europe-west1.run.app/ask';

const PLACEHOLDERS: Record<string, string> = {
  EN: 'Ask anything about our instruments...',
  DE: 'Fragen Sie uns zu unseren Instrumenten...',
  FR: 'Posez une question sur nos instruments...',
  ES: 'Pregunta sobre nuestros instrumentos...',
  GA: 'Cuir ceist faoinár n-ionstraimí...',
  PL: 'Zadaj pytanie o nasze instrumenty...',
  RO: 'Intreaba despre instrumentele noastre...',
  PT: 'Pergunte sobre nossos instrumentos...',
  HI: 'हमारे वाद्ययंत्रों के बारे में पूछें...',
  ZH: '询问我们的乐器...',
};

function parseMarkdown(text: string): React.ReactNode[] {
  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  let key = 0;

  for (const line of lines) {
    const trimmed = line.trim();

    // Heading (#, ##, ###)
    if (/^#{1,3}\s/.test(trimmed)) {
      const content = trimmed.replace(/^#{1,3}\s+/, '');
      nodes.push(
        <span key={key++} style={{ display: 'block', fontWeight: 700, fontSize: '1.05em', margin: '8px 0 2px' }}>
          {parseInline(content)}
        </span>
      );
    // Numbered list (1. 2. ...)
    } else if (/^\d+\.\s/.test(trimmed)) {
      const m = trimmed.match(/^(\d+)\.\s+(.*)$/)!;
      nodes.push(
        <span key={key++} style={{ display: 'block', margin: '3px 0' }}>
          <strong>{m[1]}.</strong> {parseInline(m[2])}
        </span>
      );
    // Bullet point
    } else if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
      const content = trimmed.slice(2);
      nodes.push(
        <li key={key++} style={{ margin: '3px 0', paddingLeft: 4 }}>
          {parseInline(content)}
        </li>
      );
    } else if (trimmed === '') {
      nodes.push(<br key={key++} />);
    } else {
      nodes.push(
        <span key={key++} style={{ display: 'block' }}>
          {parseInline(trimmed)}
        </span>
      );
    }
  }
  return nodes;
}

function parseInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Match [text](url) and **bold**
  const re = /(\[([^\]]+)\]\((https?:\/\/[^)]+)\)|\*\*([^*]+)\*\*|(https?:\/\/[^\s)]+))/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    if (match[2] && match[3]) {
      // Link
      parts.push(
        <a key={key++} href={match[3]} target="_blank" rel="noreferrer"
          style={{ color: '#8B1A1A', fontWeight: 700, textDecoration: 'underline' }}>
          {match[2]}
        </a>
      );
    } else if (match[4]) {
      // Bold
      parts.push(<strong key={key++}>{match[4]}</strong>);
    } else if (match[5]) {
      // Bare URL
      parts.push(
        <a key={key++} href={match[5]} target="_blank" rel="noreferrer"
          style={{ color: '#8B1A1A', fontWeight: 700, textDecoration: 'underline' }}>
          {match[5]}
        </a>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parts.push(text.slice(last));
  }
  return parts;
}

interface Message {
  role: 'user' | 'agent';
  text: string;
}

export const SearchAgent = ({ currentLang }: { currentLang: string }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const placeholder = PLACEHOLDERS[currentLang] || PLACEHOLDERS['EN'];

  const ask = async () => {
    const question = input.trim();
    if (!question || loading) return;
    const newMessages: Message[] = [...messages, { role: 'user', text: question }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(AGENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          lang: currentLang,
          history: messages.map(m => ({
            role: m.role === 'user' ? 'user' : 'model',
            text: m.text
          }))
        }),
      });
      const data = await res.json();
      setMessages([...newMessages, { role: 'agent', text: data.answer || 'Sorry, I could not find an answer.' }]);
    } catch {
      setMessages([...newMessages, { role: 'agent', text: 'Connection error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {messages.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 8, maxHeight: 400, overflowY: 'auto', padding: '4px 0' }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '85%', padding: '10px 14px', fontSize: 13, lineHeight: 1.6,
                background: msg.role === 'user' ? '#8B1A1A' : '#F4F1E1',
                color: msg.role === 'user' ? 'white' : '#0a0c0a',
                border: msg.role === 'agent' ? '1px solid rgba(0,0,0,0.1)' : 'none',
              }}>
                {msg.role === 'agent' ? parseMarkdown(msg.text) : msg.text}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ padding: '10px 14px', background: '#F4F1E1', border: '1px solid rgba(0,0,0,0.1)', fontSize: 13, color: 'rgba(0,0,0,0.4)' }}>
                Thinking...
              </div>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && ask()}
          placeholder={placeholder}
          style={{ flex: 1, border: '1px solid rgba(0,0,0,0.15)', padding: '10px 14px', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: 'white' }}
        />
        <button onClick={ask} disabled={loading}
          style={{ background: '#8B1A1A', color: 'white', border: 'none', padding: '10px 20px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
          Ask
        </button>
      </div>
    </div>
  );
};
